import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { ContributionForm, emptyContributionForm, type ContributionFormValues } from '../components/ContributionForm';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface ContributionRow {
  id: number;
  member_id: number;
  contribution_date: string;
  employer_amount: string;
  employee_amount: string;
  interest_amount: string;
  reference: string | null;
  notes: string | null;
  source: 'manual' | 'import' | 'seed';
}

interface ImportRowError {
  row: number;
  email: string | null;
  errors: string[];
}

function toFormValues(row: ContributionRow): ContributionFormValues {
  return {
    contribution_date: row.contribution_date,
    employer_amount: row.employer_amount,
    employee_amount: row.employee_amount,
    interest_amount: row.interest_amount,
    reference: row.reference ?? '',
    notes: row.notes ?? '',
  };
}

export function MemberContributions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useConfig();
  const confirmDelete = useConfirm();
  const { showError, showSuccess } = useToast();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ContributionRow | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<ImportRowError[]>([]);

  const filters = useMemo(() => ({}), []);
  const { data, loading, error, setPage, reload } = usePaginatedList<ContributionRow>(`/members/${id}/contributions`, filters);

  const submitAdd = async (values: ContributionFormValues) => {
    try {
      await api.post(`/members/${id}/contributions`, values);
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const submitEdit = async (values: ContributionFormValues) => {
    if (!editing) return;
    try {
      await api.put(`/members/${id}/contributions/${editing.id}`, values);
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (row: ContributionRow) => {
    if (!(await confirmDelete(`Delete the contribution dated ${row.contribution_date}?`))) return;
    try {
      await api.delete(`/members/${id}/contributions/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const onImportFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] ?? null);
    setImportErrors([]);
  };

  const submitImport = async () => {
    if (!importFile) return;
    const form = new FormData();
    form.append('file', importFile);
    try {
      const res = await api.post<{ imported: number }>('/members/contributions/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSuccess(`Imported ${res.data.imported} contribution(s).`);
      setImporting(false);
      setImportFile(null);
      setImportErrors([]);
      reload();
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.errors)) {
        setImportErrors(err.errors as unknown as ImportRowError[]);
      } else {
        showError(err instanceof ApiError ? err.message : 'Import failed.');
      }
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Contributions</h1>
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to member" onClick={() => navigate(`/memberships/${id}`)} />
        </div>
      </header>

      <div className="toolbar">
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
          + Add
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            setImportFile(null);
            setImportErrors([]);
            setImporting(true);
          }}
        >
          Import CSV
        </button>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Employer</th>
                <th>Employee</th>
                <th>Interest</th>
                <th>Reference</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.id}>
                    <td>{row.contribution_date}</td>
                    <td>{formatMoney(Number(row.employer_amount), settings.currency)}</td>
                    <td>{formatMoney(Number(row.employee_amount), settings.currency)}</td>
                    <td>{formatMoney(Number(row.interest_amount), settings.currency)}</td>
                    <td className="muted">{row.reference}</td>
                    <td className="muted">{row.source}</td>
                    <td className="right">
                      <IconButton icon={IconEdit} label="Edit" onClick={() => setEditing(row)} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No contributions recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add contribution" onClose={() => setAdding(false)}>
          <ContributionForm initial={emptyContributionForm} errors={errors} onCancel={() => setAdding(false)} onSubmit={submitAdd} />
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit contribution — ${editing.contribution_date}`} onClose={() => setEditing(null)}>
          <ContributionForm
            initial={toFormValues(editing)}
            errors={errors}
            onCancel={() => setEditing(null)}
            onSubmit={submitEdit}
          />
        </Modal>
      )}

      {importing && (
        <Modal title="Import contributions from CSV" onClose={() => setImporting(false)}>
          <p className="muted">
            Columns: <code>email, contribution_date, employer_amount, employee_amount, interest_amount</code> (optional:{' '}
            <code>reference, notes</code>). Dates must be <code>YYYY-MM-DD</code>. If any row fails validation, nothing is
            imported.
          </p>
          <input type="file" accept=".csv,text/csv" onChange={onImportFileSelected} />
          {importErrors.length > 0 && (
            <div className="alert alert-error" style={{ marginTop: 12 }}>
              <strong>{importErrors.length} row(s) failed validation — nothing was imported:</strong>
              <ul>
                {importErrors.map((e) => (
                  <li key={e.row}>
                    Row {e.row} ({e.email ?? 'no email'}): {e.errors.join(' ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setImporting(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={!importFile} onClick={submitImport}>
              Import
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
